import { Injectable, OnDestroy } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, Validators } from "@angular/forms";
import { ContentServiceValue, ContentValue, Nationality } from "@fabric-types/svc-content";
import { select, Store } from "@ngrx/store";
import {
	Address,
	CoreState,
	Customer,
	CUSTOMER_MANAGEMENT_MESSAGE_CODES,
	CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT,
	CustomerManagementAction,
	CustomerManagementSelector,
	Jurisdiction,
	JurisdictionSelector,
	Message,
	MessageAction,
	MessageService,
	MessageType,
	REMOVE_CONTENT_IN_BRACKETS_REGEX,
} from "@obg/ngx.core";
import { DocumentRef, validationRegex as globalValidationRegex } from "@obg/ngx.global";
import { ignoreNil } from "@obg/rxjs";
import { isEqual } from "lodash-es";
import { merge, Observable, Subscription } from "rxjs";
import { filter, map, switchMap, tap, withLatestFrom } from "rxjs/operators";

import { noSpecialCharactersValidator } from "../validators/no-special-characters/no-special-characters.validator";
import { belgianNationalRegistryNumberValidator } from "../validators/personal-number/belgian-national-registry-number.validator";
import { validationRegex } from "../validators/validation.const";
import { OBG_VALIDATORS } from "../validators/validators";
import { COMMON_VALIDATORS } from "../validators/validators.common";
import { ksaZipCodeValidator } from "../validators/zip-code/ksa/ksa-zip-code.validator";
import { zipCodeValidator } from "../validators/zip-code/zip-code.validator";

import {
	KsaAddressFormGroup,
	KsaPersonFormGroup,
	MyProfileFormGroup,
	SubscriptionsFormGroup,
} from "./my-profile.model";

/**
 * This service is meant to be be consumed by a component that manages changing of customer data.
 * Register the service as a provider for the top most component that is interacting with the form.
 */
@Injectable()
export class MyProfileService implements OnDestroy {
	readonly countryName$: Observable<string>;
	readonly birthCountryName$: Observable<string>;
	readonly nationalityName$: Observable<string>;
	readonly nationalities$: Observable<Nationality[]>;
	readonly customer$: Observable<Customer>;
	readonly customerMessages$: Observable<Message[]>;
	readonly isLoading$: Observable<boolean>;
	readonly form = this.createForm();

	private jurisdiction: Jurisdiction = Jurisdiction.Mga;
	private customer: Customer | undefined;
	private data$$: Subscription | undefined;

	constructor(
		private messageService: MessageService,
		private messageAction: MessageAction,
		private store: Store<CoreState>,
		private formBuilder: UntypedFormBuilder,
		private customerAction: CustomerManagementAction,
		private documentRef: DocumentRef
	) {
		this.customerMessages$ = merge(
			this.messageService.consume$({
				context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
				messageCode: CUSTOMER_MANAGEMENT_MESSAGE_CODES.updateSuccess,
			}),
			this.messageService.consume$({
				context: CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer,
				messageType: MessageType.Error,
			})
		);

		this.isLoading$ = this.store.select(CustomerManagementSelector.isBusy);

		this.customer$ = this.store.select(CustomerManagementSelector.getCustomer).pipe(
			ignoreNil(),
			tap((customer) => (this.customer = customer)),
			filter((customer) => !!customer.address.country),
			tap((customer) => this.form.reset(customer))
		) as Observable<Customer>;

		this.countryName$ = this.customer$.pipe(
			map((customer) => customer.address.country),
			ignoreNil(),
			switchMap((countryCode) =>
				new Observable(
					this.documentRef.obgEventBus.retrieve(ContentValue.getCountryByCode, {
						countryCode: countryCode,
					})
				).pipe(ignoreNil())
			),
			map((country) => country.name)
		);

		this.birthCountryName$ = this.customer$.pipe(
			map((customer) => customer.profile.birthCountry),
			ignoreNil(),
			switchMap((countryCode) =>
				new Observable(
					this.documentRef.obgEventBus.retrieve(ContentValue.getCountryByCode, {
						countryCode: countryCode,
					})
				).pipe(ignoreNil())
			),
			map((country) => country.name)
		);

		this.nationalityName$ = this.customer$.pipe(
			map((customer) => customer.profile.nationality),
			ignoreNil(),
			switchMap((countryCode) =>
				new Observable(
					this.documentRef.obgEventBus.retrieve(ContentValue.getCountryByCode, {
						countryCode: countryCode,
					})
				).pipe(ignoreNil())
			),
			map((country) => country.name)
		);

		this.nationalities$ = new Observable(
			this.documentRef.obgEventBus.retrieve(ContentServiceValue.GetAllNationalities)
		).pipe(ignoreNil());

		const jurisdiction$ = this.store.pipe(
			select(JurisdictionSelector.get),
			ignoreNil(),
			tap((jurisdiction) => {
				this.jurisdiction = jurisdiction;
				this.addFormControlsByJurisdiction(jurisdiction);
				this.patchFormControlsByJurisdiction(jurisdiction);
			})
		);

		const nationalityCheck$ = this.customer$.pipe(
			withLatestFrom(this.nationalities$),
			tap(([customer, nationalities]) => {
				const customerNationality = customer.profile.nationality;
				if (
					customerNationality &&
					!nationalities.some(
						(nationality) => nationality.value.toLowerCase() === customerNationality.toLowerCase()
					)
				) {
					this.form.controls.profile?.controls?.nationality?.setValue(undefined);
				}
			})
		);

		this.data$$ = merge(jurisdiction$, nationalityCheck$).subscribe();
	}

	/**
	 * Call `initialize` before interacting with the service.
	 */
	initialize() {
		this.store.dispatch(this.customerAction.getCustomer());
	}

	reset() {
		this.form.reset(this.customer);
	}

	hasChanges() {
		if (!this.customer) {
			return false;
		}

		const address = this.customer.address;

		return Object.keys(this.form.controls.address.value).some((key) => {
			let srcProperty;
			if (address.hasOwnProperty(key)) {
				srcProperty = address[key as keyof Address];
			}
			const cmpProperty = this.form.controls.address.value[key];

			return !this.blankOrNilEquals(srcProperty, cmpProperty) && !isEqual(srcProperty, cmpProperty);
		});
	}

	/**
	 * When the service is injected at component level, Angular will call `ngOnDestroy` when the component is destroyed.
	 */
	ngOnDestroy() {
		this.store.dispatch(this.messageAction.clearByContext(CUSTOMER_MANAGEMENT_MESSAGE_CONTEXT.customer));
		this.data$$?.unsubscribe();
	}

	/**
	 * Save changes on the server. It won't try saving the form if it is invalid.
	 */
	saveChanges() {
		if (this.form.invalid) {
			return;
		}
		const formValue = this.createSubmitPayload();
		this.store.dispatch(this.customerAction.updateCustomer(formValue));
	}

	isFieldValueRequired(control: AbstractControl): boolean {
		if (!control) {
			return false;
		}

		if (control.validator) {
			const validator = control.validator({} as AbstractControl);
			if (validator?.required) {
				return true;
			}
		}

		return false;
	}

	private createForm(): MyProfileFormGroup {
		return this.formBuilder.group({
			address: this.formBuilder.group({
				street: this.formBuilder.control({ value: "", disabled: true }, [
					Validators.required,
					Validators.minLength(2),
					Validators.maxLength(250),
					OBG_VALIDATORS.whitespace,
				]),
				zipCode: this.formBuilder.control({ value: "", disabled: true }, COMMON_VALIDATORS.zipCodeValidators),
				city: this.formBuilder.control({ value: "", disabled: true }, [
					Validators.required,
					Validators.minLength(2),
					Validators.maxLength(50),
					OBG_VALIDATORS.whitespace,
				]),
			}),
		}) as MyProfileFormGroup;
	}

	private addFormControlsByJurisdiction(jurisdiction: Jurisdiction) {
		const addressFormGroup = this.form.controls.address;

		if (jurisdiction === Jurisdiction.Dgoj) {
			addressFormGroup.addControl(
				"province",
				new UntypedFormControl({ value: "", disabled: true }, [
					Validators.required,
					Validators.minLength(2),
					Validators.maxLength(20),
					OBG_VALIDATORS.whitespace,
					noSpecialCharactersValidator(validationRegex.noSpecialCharacters.dgoj),
				])
			);

			addressFormGroup.addControl("fiscalRegionCode", new UntypedFormControl({ value: "", disabled: true }));
		}

		if (jurisdiction === Jurisdiction.Ksa) {
			this.form.addControl(
				"profile",
				this.formBuilder.group({
					nationality: this.formBuilder.control({ value: "", disabled: true }, []),
				})
			);
			addressFormGroup.addControl("houseNumber", new UntypedFormControl({ value: "", disabled: true }, []));
			addressFormGroup.addControl("additive", new UntypedFormControl({ value: "", disabled: true }, []));
			addressFormGroup.addControl("zipCode", new UntypedFormControl({ value: "", disabled: true }, []));
			addressFormGroup.addControl("street", new UntypedFormControl({ value: "", disabled: true }, []));
			addressFormGroup.addControl("city", new UntypedFormControl({ value: "", disabled: true }, []));
		}

		if (jurisdiction === Jurisdiction.Lotba) {
			this.form.addControl(
				"profile",
				this.formBuilder.group({
					occupation: this.formBuilder.control({ value: "", disabled: true }, [
						Validators.required,
						Validators.minLength(2),
						Validators.maxLength(20),
						OBG_VALIDATORS.whitespace,
					]),
					maritalStatus: this.formBuilder.control({ value: "", disabled: true }, [
						Validators.required,
						Validators.minLength(2),
						Validators.maxLength(20),
						OBG_VALIDATORS.whitespace,
					]),
				})
			);
			addressFormGroup.addControl(
				"province",
				new UntypedFormControl({ value: "", disabled: true }, [
					Validators.required,
					Validators.minLength(2),
					Validators.maxLength(50),
					OBG_VALIDATORS.whitespace,
					noSpecialCharactersValidator(validationRegex.noSpecialCharacters.lotba),
				])
			);
			addressFormGroup.addControl(
				"houseNumber",
				new UntypedFormControl({ value: "", disabled: true }, [
					Validators.required,
					Validators.min(1),
					Validators.maxLength(12),
					OBG_VALIDATORS.numeric,
				])
			);
		}

		if (jurisdiction === Jurisdiction.Aams) {
			this.form.addControl(
				"subscriptions",
				this.formBuilder.group({
					phoneExtension: new UntypedFormControl({ value: "", disabled: true }),
					phoneNumber: this.formBuilder.control({ value: "", disabled: true }, [
						Validators.required,
						Validators.minLength(8),
						Validators.maxLength(15),
						OBG_VALIDATORS.numeric,
					]),
				})
			);
			addressFormGroup.addControl("province", new UntypedFormControl({ value: "", disabled: true }, []));
			addressFormGroup.addControl("provinceCode", new UntypedFormControl({ value: "", disabled: true }, []));
			addressFormGroup.addControl("regionId", new UntypedFormControl({ value: "", disabled: true }, []));
		}

		if (jurisdiction === Jurisdiction.Iplyc || jurisdiction === Jurisdiction.LPCSE) {
			this.form.addControl(
				"profile",
				this.formBuilder.group({
					occupation: this.formBuilder.control({ value: "", disabled: true }, [
						Validators.required,
						Validators.minLength(2),
						Validators.maxLength(20),
						OBG_VALIDATORS.whitespace,
					]),
					maritalStatus: this.formBuilder.control({ value: "", disabled: true }, [
						Validators.required,
						Validators.minLength(2),
						Validators.maxLength(20),
						OBG_VALIDATORS.whitespace,
					]),
				})
			);
			addressFormGroup.addControl(
				"province",
				new UntypedFormControl({ value: "", disabled: true }, [
					Validators.required,
					Validators.minLength(2),
					Validators.maxLength(50),
					OBG_VALIDATORS.whitespace,
					noSpecialCharactersValidator(validationRegex.noSpecialCharacters.iplyc),
				])
			);
		}

		if (jurisdiction === Jurisdiction.Segob) {
			this.form.addControl(
				"profile",
				this.formBuilder.group({
					occupation: this.formBuilder.control({ value: "", disabled: true }, [Validators.required]),
					nationality: this.formBuilder.control({ value: "", disabled: true }, [Validators.required]),
					personalCode: this.formBuilder.control({ value: "", disabled: true }, [
						Validators.minLength(18),
						Validators.maxLength(18),
						Validators.pattern(globalValidationRegex.alphanumeric),
					]),
					taxCode: this.formBuilder.control({ value: "", disabled: true }, [
						Validators.minLength(12),
						Validators.maxLength(13),
						Validators.pattern(globalValidationRegex.alphanumeric),
					]),
				})
			);
		}

		if (jurisdiction === Jurisdiction.Conajzar) {
			this.form.addControl(
				"profile",
				this.formBuilder.group({
					nationality: this.formBuilder.control({ value: "", disabled: true }),
					occupation: this.formBuilder.control({ value: "", disabled: true }, [Validators.required]),
					personalCodeType: this.formBuilder.control({ value: "", disabled: true }),
					personalCode: this.formBuilder.control({ value: "", disabled: true }),
				})
			);
		}

		if (jurisdiction === Jurisdiction.BGC) {
			this.form.addControl(
				"profile",
				this.formBuilder.group({
					personalCode: this.formBuilder.control({ value: "", disabled: true }, [
						Validators.required,
						belgianNationalRegistryNumberValidator,
					]),
				})
			);
		}

		if (jurisdiction === Jurisdiction.COGA) {
			this.form.addControl(
				"subscriptions",
				this.formBuilder.group({
					phoneExtension: new UntypedFormControl({ value: "", disabled: true }),
					phoneNumber: this.formBuilder.control({ value: "", disabled: true }, [
						Validators.required,
						OBG_VALIDATORS.numeric,
						Validators.minLength(7),
						Validators.maxLength(12),
					]),
				})
			);
			addressFormGroup.addControl(
				"residenceMunicipality",
				new UntypedFormControl({ value: "", disabled: true }, [Validators.required])
			);
			addressFormGroup.addControl(
				"departmentOfResidence",
				new UntypedFormControl({ value: "", disabled: true }, [Validators.required])
			);
			addressFormGroup.addControl(
				"zipCode",
				new UntypedFormControl({ value: "", disabled: true }, [Validators.required])
			);
		}

		if (jurisdiction === Jurisdiction.Pega) {
			addressFormGroup.addControl(
				"department",
				new UntypedFormControl({ value: "", disabled: true }, [Validators.required])
			);
			addressFormGroup.addControl(
				"province",
				new UntypedFormControl({ value: "", disabled: true }, [Validators.required])
			);
			addressFormGroup.addControl(
				"district",
				new UntypedFormControl({ value: "", disabled: true }, [Validators.required])
			);
		}

		return;
	}

	private patchFormControlsByJurisdiction(jurisdiction: Jurisdiction) {
		const addressFormGroup = this.form.controls.address;

		const streetControl = this.form.controls.address.controls.street;
		const cityControl = this.form.controls.address.controls.city;
		const zipCodeControl = this.form.controls.address.controls.zipCode;

		if (jurisdiction === Jurisdiction.Dgoj) {
			streetControl.setValidators([
				Validators.required,
				Validators.minLength(2),
				Validators.maxLength(250),
				OBG_VALIDATORS.whitespace,
				noSpecialCharactersValidator(validationRegex.noSpecialCharacters.dgoj),
			]);

			cityControl?.setValidators([
				Validators.required,
				Validators.minLength(2),
				Validators.maxLength(20),
				OBG_VALIDATORS.whitespace,
				noSpecialCharactersValidator(validationRegex.noSpecialCharacters.dgoj),
			]);
		}

		if (jurisdiction === Jurisdiction.Ksa) {
			const profile = this.form.controls.profile as KsaPersonFormGroup;
			const address = this.form.controls.address as KsaAddressFormGroup;

			zipCodeControl.setValidators([Validators.required, ksaZipCodeValidator]);

			address.controls.houseNumber.setValidators([
				Validators.required,
				Validators.maxLength(6),
				OBG_VALIDATORS.numeric,
			]);

			address.controls.additive.setValidators(Validators.maxLength(6));
			profile.controls.nationality.setValidators(Validators.required);
		}

		if (jurisdiction === Jurisdiction.Iaui) {
			zipCodeControl.setValidators([...COMMON_VALIDATORS.zipCodeValidators, zipCodeValidator(true)]);
		}

		if (
			jurisdiction === Jurisdiction.Lotba ||
			jurisdiction === Jurisdiction.Iplyc ||
			jurisdiction === Jurisdiction.LPCSE
		) {
			zipCodeControl.setValidators([
				Validators.required,
				Validators.minLength(4),
				Validators.maxLength(8),
				noSpecialCharactersValidator(validationRegex.noSpecialCharacters.generic),
			]);
		}

		if (jurisdiction === Jurisdiction.Mta) {
			zipCodeControl.setValidators([
				Validators.required,
				OBG_VALIDATORS.numeric,
				Validators.minLength(5),
				Validators.maxLength(5),
				noSpecialCharactersValidator(validationRegex.noSpecialCharacters.generic),
			]);
		}

		if (jurisdiction === Jurisdiction.Aams) {
			streetControl.setValidators([
				Validators.required,
				Validators.minLength(2),
				Validators.maxLength(250),
				OBG_VALIDATORS.whitespace,
				noSpecialCharactersValidator(validationRegex.noSpecialCharacters.generic),
			]);

			zipCodeControl.setValidators([
				Validators.required,
				Validators.minLength(5),
				Validators.maxLength(5),
				OBG_VALIDATORS.numeric,
			]);

			cityControl?.setValidators([Validators.required, OBG_VALIDATORS.whitespace]);
		}

		if (jurisdiction === Jurisdiction.COGA) {
			streetControl.setValidators([
				Validators.required,
				Validators.minLength(2),
				Validators.maxLength(250),
				OBG_VALIDATORS.whitespace,
				noSpecialCharactersValidator(validationRegex.noSpecialCharacters.generic),
			]);

			zipCodeControl.setValidators([Validators.required]);

			cityControl?.clearValidators();
		}

		if (jurisdiction === Jurisdiction.Pega) {
			streetControl.setValidators([
				Validators.required,
				OBG_VALIDATORS.whitespace,
				Validators.minLength(2),
				Validators.maxLength(250),
				noSpecialCharactersValidator(validationRegex.noSpecialCharacters.pega),
			]);
		}

		if (jurisdiction === Jurisdiction.Conajzar) {
			streetControl.setValidators([
				Validators.required,
				OBG_VALIDATORS.whitespace,
				Validators.minLength(2),
				Validators.maxLength(150),
			]);
		}

		addressFormGroup.updateValueAndValidity();

		return;
	}

	truncateProvinceCodeFromCity(city: string): string {
		const lastCommaIndex = city.lastIndexOf(",");
		return (city = lastCommaIndex !== -1 ? city.slice(0, lastCommaIndex).trim() : city);
	}

	private createSubmitPayload() {
		const payload = this.form.value;

		if (this.jurisdiction === Jurisdiction.Aams) {
			payload.address.city = payload.address.city?.replace(REMOVE_CONTENT_IN_BRACKETS_REGEX, "");
			if (payload.subscriptions) {
				const { phoneExtension, phoneNumber } = (this.form.controls.subscriptions as SubscriptionsFormGroup)
					.controls;
				payload.subscriptions.phoneNumber = `+${phoneExtension.value}${phoneNumber.value}`;
			}
		}

		if (this.jurisdiction === Jurisdiction.COGA && payload.subscriptions) {
			const { phoneExtension, phoneNumber } = (this.form.controls.subscriptions as SubscriptionsFormGroup)
				.controls;
			payload.subscriptions.phoneNumber = `+${phoneExtension.value}${phoneNumber.value}`;
			delete payload.subscriptions.phoneExtension;
		}

		if (this.form.controls.address.controls.province && payload.address) {
			payload.address.province = this.form.controls.address.controls.province?.getRawValue();
			payload.address.city = this.truncateProvinceCodeFromCity(payload.address.city);
		}

		return payload;
	}

	private blankOrNilEquals = (compare: any, src: any) => {
		return this.isBlankOrNil(src) && this.isBlankOrNil(compare);
	};

	private isBlankOrNil = (obj: any) => {
		return obj === undefined || obj === null || obj === "";
	};
}
